import { useEffect, useRef, useState } from "react";
import { animated, useSpring } from "@react-spring/web";
import { useParams, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useSpeech } from "../../container/SpeechContext.jsx";
import Text from "./Text.jsx";
import { Chat } from "../../models/chat.js";
import { ColorConfig } from "../../models/colorConfig.js";
import { getTopicsConfig } from "../Topics/Topics.config.js";
import ChatInput from "./ChatInput.jsx";
import { updateProgress } from "../../redux/ducks/progressSlice.js";
import scripts from "./scripts.js";
import { addUnscriptedEntry } from "../../redux/ducks/unscriptedSlice.js";
import rightInput from "../../assets/audio/rightinput.mp3";
import wrongInput from "../../assets/audio/wronginput.mp3";
import receivedSound from "../../assets/audio/message.mp3";
import questions from "./questions.js";
import "./ChatTopic.scss";
import { getCharacterDescriptions } from "../../services/audioDescription.js";
import { useAttractContext } from "../Attract/AttractManager.jsx";
import MoreMessages from "./MoreMessages.jsx";
import {
  shouldScroll,
  interpolateMessages,
  isEndOfThreadResponse,
  getUserMessageConfig,
  buildResponseFormData,
  buildResponseMessageConfig,
} from "./ChatTopic.service.js";
import {
  setHasGoneThroughOnboarding,
  setHasHeardCharacterDescriptions,
  setLang,
} from "../../redux/ducks/settingsSlice.js";
// eslint-disable-next-line no-unused-vars
import {
  clear,
  getAllMessages,
  getLastItem,
  getLog,
  remove,
  silentAnnounce,
} from "../../services/announcerService.js";
import { api } from "../../services/apiService.js";
import { useCustomNavigate } from "../../container/useCustomNavigate.js";
import { getString } from "../../services/stringsHelpers.js";
import { useLanguage } from "../../container/LanguageContext.jsx";

const userColor = new ColorConfig({
  primary: "#232232",
  rangeStart: "#E0E0E9",
  rangeEnd: "#EDEDF0",
});

const INTRO_TIMING = 29 * 1000;

/**
 * Clears the tabindex attribute off of announcer message elements
 * @param {"assertive" | "polite" | "focus" | ""} level
 */
function clearTabIndex(level) {
  const msgs = getAllMessages(level);
  if (msgs) {
    for (let i = 0; i < msgs.length; i += 1) {
      msgs[i].removeAttribute("tabindex");
    }
  }
}

/**
 * The chat topic component
 * @component
 */
function ChatTopic() {
  const { topic = "onboarding" } = useParams();
  const [params] = useSearchParams();
  const navigate = useCustomNavigate();
  const progressIdx = useSelector(
    /**
     * @param {import("../../redux/store.js").ReduxState} state
     */
    (state) => {
      if (topic && topic.toLowerCase() in state.progress) {
        // @ts-ignore
        return state.progress[topic.toLowerCase()];
      }
      return null;
    }
  );
  const unscripted = useSelector(
    /**
     * @param {import("../../redux/store.js").ReduxState} state
     */
    (state) => {
      if (topic && topic.toLowerCase() in state.unscripted) {
        // @ts-ignore
        return state.unscripted[topic.toLowerCase()];
      }
      return [];
    }
  );
  const {
    isUsingKeypad,
    hasHeardCharacterDescriptions,
    isSfxOn,
    hasGoneThroughOnboarding,
  } = useSelector(
    /**
     * @param {import("../../redux/store.js").ReduxState} state
     */
    (state) => state.settings
  );
  const dispatch = useDispatch();
  const [allMessages, setAllMessages] = useState(/** @type {Chat[]} */ ([]));
  const { strings, lang } = useLanguage();
  const [openForInput, setOpenForInput] = useState(false);
  /** @type {React.RefObject<HTMLElement>} */
  const scrollRef = useRef(null);
  const { triggerHeartbeat } = useAttractContext();
  const { speak, cancel, supported, exportAllText } = useSpeech();
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const hasPerformedInitialLoad = useRef(false);
  const hasSpoken = useRef(false);
  const topicConfig = getTopicsConfig(strings).find(
    (config) => config.key === topic.toLowerCase()
  );

  const [fadeSpringStyles, fadeSpringApi] = useSpring(
    () => ({
      opacity: 1,
    }),
    [topic]
  );

  const [moveSpringStyles, moveSpringApi] = useSpring(
    () => ({
      x: 0,
    }),
    [topic]
  );

  /**
   * What if we previously left after an unanswered question? We need to open
   * this up for input if we have a question with no valid responses after it
   */
  useEffect(() => {
    setShouldAutoScroll(() =>
      shouldScroll(document.querySelector(".chat__topic__messages"))
    );
    scrollToBottom();
    if (allMessages.length && !hasPerformedInitialLoad.current) {
      // handle the announcer logic on load
      handleAnnouncerOnLoad();

      // determine if we should show question response options or not
      handleShouldOpenInputOnLoad();

      // we've performed that initial load, so we should no longer perform
      // these one-time operations
      hasPerformedInitialLoad.current = true;
    }

    if (allMessages.length && hasPerformedInitialLoad.current) {
      // We've performed the initial load, so we have all the dom elements we
      // need to make this happen
      handleSizeAnnouncerElements();
    }
    // NOTE: onLoad, allMessages is empty, so we need to add this dependency
  }, [allMessages, allMessages.length]);

  /**
   * Handle the logic behind the announcer div on initial load
   * - Remove the existing announcer, which on every other page is in another location in the DOM. This is to pin the hidden div while scrolling
   * - Seed the div with existing messages so we can overlay them
   *   - Remove the log role first so we don't read all those new messages
   *   - Return the log role so we read new messages before they hit
   */
  function handleAnnouncerOnLoad() {
    remove();
    if (progressIdx !== null) {
      allMessages.slice(0, progressIdx - 1).forEach((message) => {
        message.announce(({ message: text }) => {
          silentAnnounce({ message: text, level: "polite" });
        }, strings);
      });
      // Oof, but I need a way to delay picking out the last element until it's
      // been rendered. The last element on initial render is handled differently,
      // to make it seem like you just joined a running conversation
      setTimeout(() => {
        const last = /** @type {HTMLElement} */ (getLastItem("polite"));
        if (last) {
          last.setAttribute("tabindex", "0");
          last.focus();
        }
      });
      setTimeout(() => {
        const l = getLog("polite");
        if (l) {
          l.setAttribute("role", "log");
          l.setAttribute("aria-live", "polite");
          l.setAttribute("aria-relevant", "additions");
        }
      });
    }
  }

  /**
   * Handle the logic behind showing or hiding the input buttons on initial load
   */
  function handleShouldOpenInputOnLoad() {
    const lastQuestion = getLastQuestion();
    const lastQuestionIdx = allMessages.findIndex((m) => m === lastQuestion);
    if (lastQuestionIdx >= 0 && lastQuestionIdx !== allMessages.length - 1) {
      for (let i = lastQuestionIdx + 1; i < allMessages.length; i += 1) {
        const thisMessage = allMessages[i];
        if (
          thisMessage.sender.name === "User" &&
          lastQuestion.validator(thisMessage.rawContent[0].children)[0]
        ) {
          handleOpenForInput(false);
          hasPerformedInitialLoad.current = true;
          return;
        }
      }
      lastQuestion.announce(({ message }) => speak({ text: message }), strings);
      handleOpenForInput(true);
    } else if (lastQuestionIdx === allMessages.length - 1) {
      lastQuestion.announce(({ message }) => speak({ text: message }), strings);
      handleOpenForInput(true);
    }
  }

  /**
   * Size announcer elements to match the positioning of the chat message
   * elements. This is predicated on the existence both sets of messages, which
   * is handled in the useEffect
   */
  function handleSizeAnnouncerElements() {
    const chatMessageNodes = document.querySelectorAll(
      ".chat__topic__messages .text:not(.announcer-text)"
    );
    if (chatMessageNodes.length) {
      // iterate over each announcer node and place them accordingly
      const announcerNodes = getAllMessages("polite");
      if (announcerNodes && announcerNodes.length) {
        for (let i = 0; i < announcerNodes.length; i += 1) {
          const announcerNode = announcerNodes[i];
          const chatMessageNode = chatMessageNodes[i];

          if (announcerNode && chatMessageNode) {
            const { width, height } = chatMessageNode.getBoundingClientRect();
            /** @type {HTMLElement} */ (
              announcerNode
            ).style.width = `${width}px`;
            /** @type {HTMLElement} */ (
              announcerNode
            ).style.height = `${height}px`;
          }
        }
      }
    }
  }

  /**
   * Update the messages array based on usage history
   */
  useEffect(() => {
    /** @type {import("./scripts.js").MessageConfig[]} */
    const messageConfigs =
      // @ts-ignore
      scripts[topic.toLowerCase()]({
        isKiosk: params.get("kiosk"),
        isDIY: params.get("diy"),
        target: params.get("target"),
        completeCb: () => dispatch(setHasGoneThroughOnboarding(true)),
        languageSelectCb: /** @param {string} language */ (language) =>
          dispatch(setLang(language)),
        strings,
        lang,
      });
    const scriptedMessages = messageConfigs.map((ea) =>
      Chat.fromScript({ ...ea, isKiosk: Boolean(params.get("kiosk")), strings })
    );
    const msgs = interpolateMessages(
      scriptedMessages,
      unscripted,
      Boolean(params.get("kiosk")),
      strings
    );
    const shouldHearCharDescriptions =
      !progressIdx && isUsingKeypad && !hasHeardCharacterDescriptions;
    if (shouldHearCharDescriptions && !hasSpoken.current) {
      speak({
        text: `${getString(
          "You're in a group chat with 4 others.",
          strings
        )} ${Object.values(getCharacterDescriptions(strings))
          .map((ea) => ea.short)
          .join(" ")}`,
      });
      hasSpoken.current = true;
      setTimeout(() => {
        setAllMessages(msgs);
      }, INTRO_TIMING);
      dispatch(setHasHeardCharacterDescriptions(true));
    } else {
      setAllMessages(msgs);
    }
    // NOTE: We include `hasGoneThroughOnboarding` in the dependency array so
    // that the chat properly resets the msgs array when the user hits "Start
    // Over" in a DIY experience. Without, since we don't leave the page, the
    // msgs array is retained and the user skips past selecting a language
  }, [topic, supported, lang, hasGoneThroughOnboarding]);

  /**
   * Add a way to know when images load in, to have an additional scroll when
   * that happens.
   */
  useEffect(() => {
    document.addEventListener("image-loaded", handleImageLoaded);
    return () =>
      document.removeEventListener("image-loaded", handleImageLoaded);

    /**
     * Image loaded event callback function - handles scrolling to the bottom
     * and sizing the announcer elements - basically anything that needs
     * to be positioned since the image loads and shifts layout after these
     * actions normally occur
     */
    function handleImageLoaded() {
      handleSizeAnnouncerElements();
      scrollToBottom();
    }
  }, [shouldAutoScroll]);

  /**
   * Handle joining topics
   * Note that we need to start by removing the existing announcer because it
   * holds a direct DOM reference. React removes this DOM reference when we
   * navigate to a new page, so we need to refresh it when joining a new topic.
   * Calling `remove()` as we do here should suffice, since calling `speak()`
   * later will create a new announcer instance if one does not exist
   */
  useEffect(() => {
    remove();
    if (!hasSpoken.current && topic !== "onboarding" && !progressIdx) {
      speak({
        text: getString("You've joined a new group chat topic.", strings),
      });
      hasSpoken.current = true;
    }
  }, [topic]);

  /**
   * Callback after a question has finished appearing to the user
   */
  function questionCallback() {
    triggerHeartbeat();
    if (isSfxOn) {
      const personaSound = new Audio(receivedSound);
      personaSound.play();
    }
    // update progress now that the user has seen the item
    dispatch(updateProgress({ [topic.toLowerCase()]: progressIdx + 1 }));
    const currentMessage = allMessages[progressIdx ?? 0];
    // remove the existing tabindex from any questions there may be for VO
    clearTabIndex("focus");
    clearTabIndex("polite");
    currentMessage.announce(({ message }) => speak({ text: message }), strings);
    handleSizeAnnouncerElements();
    // set the app up to accept input
    handleOpenForInput(true);
    // We need to focus the last item when it comes up. If we don't, then
    // VoiceOver/TalkBack will read the entire conversation from the top
    const last = getLastItem();
    if(last) {
      last.setAttribute("tabindex", 0);
      last.focus();
    }
    // alert the user that there are additional messages available to view
    if (scrollRef.current) {
      if (!shouldScroll(scrollRef.current)) {
        setShouldAutoScroll(false);
      }
    }
  }

  /**
   * Scroll the scrollref to the bottom without user interaction
   * @param {boolean} force - force a scroll even if the `shouldAutoScroll` state is false
   */
  function scrollToBottom(force = false) {
    if (scrollRef.current && (shouldAutoScroll || force)) {
      scrollRef.current.scrollTo(0, scrollRef.current.scrollHeight);
    }
  }

  /**
   * Callback after a text has finished appearing to the user
   */
  function viewedCallback() {
    triggerHeartbeat();
    if (isSfxOn) {
      const personaSound = new Audio(receivedSound);
      personaSound.play();
    }
    // update progress now that the user has seen the item
    dispatch(updateProgress({ [topic.toLowerCase()]: progressIdx }));
    const currentMessage = allMessages[progressIdx ?? 0];
    currentMessage.announce(
      /** @type {(args: { message: string}) => void} */
      ({ message }) => speak({ text: message }),
      strings
    );
    if (progressIdx === null) {
      // if we are here, then we just saw the first message and we need to add
      // focus to prevent re-reading from the top
      const last = getLastItem();
      if (last) {
        last.setAttribute("tabindex", 0);
        last.focus();
      }
    }
    handleSizeAnnouncerElements();
    // get the next item, if any is available
    const nextMessage = allMessages[progressIdx + 1];
    // if there is, add it to the messages
    if (nextMessage) {
      setTimeout(() => {
        // update progress now that the user has seen the item
        dispatch(updateProgress({ [topic.toLowerCase()]: progressIdx + 1 }));
      }, currentMessage.duration(isUsingKeypad ? 200 : 50));
    }
    // alert the user that there are additional messages available to view
    if (scrollRef.current) {
      if (!shouldScroll(scrollRef.current)) {
        setShouldAutoScroll(false);
      }
    }
  }

  /**
   * User input handler
   * @param {import("../../models/questionOption.js").Option} option - the text as submitted by the user
   */
  async function handleSubmit(option) {
    const lastQuestion = getLastQuestion();
    const userMessageConfig = getUserMessageConfig({
      text: option.letter,
      lastQuestion,
    });

    const userChat = Chat.fromScript({
      ...userMessageConfig,
      isKiosk: Boolean(params.get("kiosk")),
      strings,
      validator: () => [false, ""],
    });

    if (!params.get("kiosk")) {
      userChat.announce(({ message }) => speak({ text: message }), strings);
    }

    // assess the input's validity
    const res = lastQuestion?.validator(option.value);

    // NOTE: Ideally the validator would with the rest of the question,
    // but right now the script is self-referential and would require a greater
    // effort to refactor than we have time (as of 2/27). Saving for later
    const [isValid, nextThread] = res ?? [false, []];
    if (isValid && openForInput) {
      if (isEndOfThreadResponse(nextThread)) {
        // NOTE: And don't forget to include the params to keep them when
        // navigating to the next section!
        fadeSpringApi.start({
          config: {
            duration: 125,
          },
          opacity: 0,
          onResolve: () => {
            setAllMessages([]);
            cancel();

            moveSpringApi.start({
              config: {
                duration: 200,
              },
              x: 1000,
              onResolve: () => {
                clear();
                cancel();
                navigate(nextThread);
              },
            });
          },
        });
        return;
      }

      const { suppressSubmission, suppressResponse } = lastQuestion;
      if (!suppressSubmission) {
        const formData = buildResponseFormData({
          lastQuestion,
          text: option.value,
          location: params.get("diy") ? "diy" : "nmnh",
        });
        api.submitResponse(formData);
      }

      if (isSfxOn) {
        const userSound = new Audio(rightInput);
        userSound.play();
      }

      let responseMessageConfig = null;
      if (!suppressResponse) {
        // if valid and open for input generate a JSON-able object
        // for Redux/localStorage

        // pick the correct/a suitable randomized response
        responseMessageConfig = await buildResponseMessageConfig({
          lastQuestion,
          text: option.value,
          strings,
        });
      }

      // close the app to input
      handleOpenForInput(false);
      // add all unscripted entries to redux/localStorage
      // need to handle for a null response for questions that suppress it
      const scriptEntries = [userMessageConfig];
      if (responseMessageConfig) {
        scriptEntries.push(responseMessageConfig);
      }

      const chatEntries = [userChat];
      if (responseMessageConfig) {
        const responseChat = Chat.fromScript({
          ...responseMessageConfig,
          isKiosk: params.get("kiosk"),
          strings,
        });
        chatEntries.push(responseChat);
        responseChat.announce(
          ({ message }) => speak({ text: message }),
          strings
        );
      }

      updateChatStates({
        scriptEntries,
        chatEntries,
        nextThread,
      });

      // prevent the window focus from leaving the window for the screen reader
      const last = getLastItem();
      if (last) {
        last.setAttribute("tabindex", 0);
        last.focus();  
      }
    
    } else {
      if (isSfxOn) {
        const userSound = new Audio(wrongInput);
        userSound.play();
      }
      // if invalid or not open for input generate a JSON-able object for Redux/localStorage
      const responseMessageConfig = {
        type: "text",
        name: lastQuestion
          ? lastQuestion.sender.name
          : scripts[topic.toLowerCase()]({
              isKiosk: params.get("kiosk"),
              isDIY: params.get("diy"),
              target: params.get("target"),
              completeCb: () => dispatch(setHasGoneThroughOnboarding(true)),
              languageSelectCb: (language) => dispatch(setLang(language)),
              strings,
              lang,
            })[0].name,
        rawContent: [
          {
            tag: "p",
            attributes: {},
            children: getString("Say what now?", strings),
          },
        ],
      };

      const responseChat = Chat.fromScript({
        ...responseMessageConfig,
        isKiosk: Boolean(params.get("kiosk")),
        strings,
      });
      responseChat.announce(({ message }) => speak({ text: message }), strings);

      updateChatStates({
        scriptEntries: [userMessageConfig, responseMessageConfig],
        chatEntries: [userChat, responseChat],
      });
    }
  }

  /**
   * Update the various states associated with a new message entry in the chat
   * @param {Object} args - object for named arguments
   * @param {import("./scripts.js").MessageConfig[]} args.scriptEntries
   * @param {Chat[]} args.chatEntries
   * @param {null | import("./scripts.js").MessageConfig[] | string} [args.nextThread=null]
   */
  function updateChatStates({ scriptEntries, chatEntries, nextThread = null }) {
    // add all unscripted entries to redux/localStorage
    dispatch(
      addUnscriptedEntry({
        topic: topic.toLowerCase(),
        index: progressIdx,
        scriptEntries,
      })
    );

    const nextChatEntries = nextThread
      ? nextThread.map((ea) =>
          Chat.fromScript({ ...ea, isKiosk: params.get("kiosk"), strings })
        )
      : [];

    setAllMessages((prev) => [
      ...prev.slice(0, progressIdx),
      ...chatEntries,
      ...nextChatEntries,
      ...prev.slice(progressIdx),
    ]);

    dispatch(
      updateProgress({
        [topic.toLowerCase()]: progressIdx + chatEntries.length,
      })
    );
  }

  /**
   * Callback when we want to handle setting the chat open for user input or not
   * @param {boolean} isOpen - whether or not we want to set the app open or not
   */
  function handleOpenForInput(isOpen) {
    if (isOpen) {
      setOpenForInput(true);
    } else {
      setOpenForInput(false);
    }
  }

  /**
   * Find the last available question we've seen and return that item
   * @returns {null | Chat}
   */
  function getLastQuestion() {
    for (let i = progressIdx; i >= 0; i -= 1) {
      if (allMessages[i]?.type === "question") {
        return allMessages[i];
      }
    }
    return null;
  }

  /**
   * The core of the app - build an array of messages to display out of the
   * collection of all available messages
   * @returns {JSX.Element[]}
   */
  function buildMessagesToShow() {
    if (allMessages.length) {
      if (progressIdx === null) {
        const nextMessage = allMessages[0];
        return [
          <Text
            color={
              nextMessage.sender.name === "User" ? userColor : topicConfig.color
            }
            message={nextMessage}
            cb={() =>
              nextMessage.type === "question"
                ? questionCallback()
                : viewedCallback()
            }
            key={`${nextMessage.toString()}-0`}
            scrollToBottom={() => scrollToBottom()}
            shouldAutoScroll={shouldAutoScroll}
          />,
        ];
      }

      const prevMessagesToShow = allMessages
        .slice(0, progressIdx)
        .map((ea) => (
          <Text
            color={ea.sender.name === "User" ? userColor : topicConfig.color}
            message={ea}
            disableTyping
            key={`${ea.toString()}-${progressIdx}${ea.id ? `-${ea.id}` : ""}`}
            scrollToBottom={() => scrollToBottom()}
            shouldAutoScroll={shouldAutoScroll}
          />
        ));
      const nextMessage = allMessages[progressIdx];
      if (nextMessage) {
        return [
          ...prevMessagesToShow,
          <Text
            color={
              nextMessage.sender.name === "User" ? userColor : topicConfig.color
            }
            message={nextMessage}
            cb={() =>
              nextMessage.type === "question"
                ? questionCallback()
                : viewedCallback()
            }
            disableTyping={nextMessage.sender.name === "User"}
            key={`${nextMessage.toString()}-${progressIdx}${
              nextMessage.id ? `-${nextMessage.id}` : ""
            }`}
            scrollToBottom={() => scrollToBottom()}
            shouldAutoScroll={shouldAutoScroll}
          />,
        ];
      }
      return prevMessagesToShow;
    }
    return [];
  }

  /**
   * Based on the last question, list the keyboard string of the available
   * fast options
   * @returns {{letter: string, text: string, value: string}[]}
   */
  function getFastOptions() {
    const lastQuestion = getLastQuestion();
    if (lastQuestion) {
      const questionObject = questions(strings).find(
        (ea) => ea.id === lastQuestion.id
      );
      return (
        questionObject?.fastOptions || [
          {
            letter: getString("Yes", strings),
            text: getString("Move to the next chat", strings),
            value: "Yes",
          },
          {
            letter: getString("No", strings),
            text: getString("Return to the topics menu", strings),
            value: "No",
          },
        ]
      );
    }
    return [];
  }

  /**
   * Set the autoscroll state based on the scroll state of the event target element
   * @param {React.UIEvent<HTMLElement, UIEvent>} e
   */
  function handleAutoScroll(e) {
    setShouldAutoScroll(() => shouldScroll(e.target));
  }

  return (
    <animated.div
      style={{ ...fadeSpringStyles, ...moveSpringStyles }}
      className="chat__topic"
    >
      <section
        className="chat__topic__messages"
        ref={scrollRef}
        key={topic}
        onScroll={(e) => handleAutoScroll(e)}
      >
        {buildMessagesToShow()}
        {!shouldAutoScroll ? (
          <MoreMessages handleClick={() => scrollToBottom(true)} />
        ) : null}
      </section>
      {process.env.REACT_APP_EXPORT ? (
        <button
          onClick={() => exportAllText()}
          type="button"
          id="export-button"
        >
          Export Text
        </button>
      ) : null}
      {openForInput ? (
        <ChatInput
          submitCb={(option) => handleSubmit(option)}
          fastOptions={openForInput ? getFastOptions() : []}
          scrollToBottom={() => scrollToBottom()}
        />
      ) : null}
    </animated.div>
  );
}

export default ChatTopic;
